import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";

// Pin colours per centre, in the order shown in the design.
export const CENTRE_COLORS = ["#1677ff", "#e8237d", "#1677ff", "#0b2fd1", "#e5262a", "#ec2f7b", "#0b5e45", "#f5a30a", "#1fb59a", "#7b2ff7"];

export const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
export const startOfDay = (d) => { const r = new Date(d); r.setHours(0, 0, 0, 0); return r; };
export const fromKey = (key) => new Date(`${key}T00:00:00`);
export const scheduleDate = (s) => fromKey(String(s.date || s.startTime).slice(0, 10));
export const idOf = (v) => (v && typeof v === "object" ? v._id || v.id : v) || "";
export const centreIdOf = (s) => idOf(s.balagruhaId) || idOf(s.balagruha);
export const coachIdOf = (s) => idOf(s.assignedTo) || idOf(s.assignedToUser);
export const hhmm = (time) => (time ? new Date(time).toTimeString().slice(0, 5) : "");
export const isStudent = (u) => u.role === "student" || u.roles?.includes("student");
export const isCoach = (u) => u.role === "coach" || u.roles?.includes("coach");

// "13:30" -> "01:30 PM"
export const ampm = (value) => {
  if (!value) return "";
  const [h, m] = value.split(":").map(Number);
  return `${String(h % 12 || 12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};
// Visit cards show "09:30 – 10:30" in 12-hour form without the meridiem, as in the design.
export const shortTime = (time) => (time ? ampm(hhmm(time)).slice(0, 5) : "");
// The design abbreviates months as "Sep" (en-GB would give "Sept").
export const monthShort = (d) => d.toLocaleDateString("en-US", { month: "short" });
export const dayMonth = (d) => `${d.getDate()} ${monthShort(d)}`;

export const rangeLabel = (start, end) => {
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  if (sameMonth) return `${start.getDate()} – ${end.getDate()} ${end.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`;
  return `${dayMonth(start)} – ${dayMonth(end)} ${end.getFullYear()}`;
};

// "Thu, 15 Oct 2026"
export const longDate = (key) => {
  const d = fromKey(key);
  return `${d.toLocaleDateString("en-GB", { weekday: "short" })}, ${dayMonth(d)} ${d.getFullYear()}`;
};

// A Balagruha's "Available Class Timing" rows ({ day: "Monday", startTime: "10:00", endTime: "12:00" }),
// set on the Balagruha form. A From-To row also has toDay ({ day: "Monday", toDay: "Friday", ... }).
const WEEK_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const dayIndex = (day) => WEEK_DAYS.findIndex((d) => d.slice(0, 3).toLowerCase() === String(day || "").trim().slice(0, 3).toLowerCase());
const minutesOf = (value) => {
  const [h, m] = String(value || "").split(":").map(Number);
  return Number.isFinite(h) ? h * 60 + (Number.isFinite(m) ? m : 0) : NaN;
};
// A From-To row counts as one row for each day in the range.
const expandDayRanges = (timings) => (Array.isArray(timings) ? timings : []).flatMap((t) => {
  const from = dayIndex(t?.day);
  const to = dayIndex(t?.toDay);
  return from >= 0 && to > from ? WEEK_DAYS.slice(from, to + 1).map((day) => ({ ...t, day })) : [t];
});
const validTimings = (timings) => expandDayRanges(timings)
  .filter((t) => dayIndex(t?.day) >= 0 && Number.isFinite(minutesOf(t.startTime)) && Number.isFinite(minutesOf(t.endTime)));

// "10:00" -> "10 AM", "13:30" -> "1:30 PM" (non-breaking space, so a narrow cell never splits "10 AM").
const compactTime = (value) => {
  const total = minutesOf(value);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h % 12 || 12}${m ? `:${String(m).padStart(2, "0")}` : ""}\u00a0${h < 12 ? "AM" : "PM"}`;
};

// [0, 1, 2, 3, 4, 6] -> "Mon–Fri, Sun"
const dayRuns = (days) => {
  const short = (i) => WEEK_DAYS[i].slice(0, 3);
  const parts = [];
  for (let i = 0; i < days.length;) {
    let j = i;
    while (j + 1 < days.length && days[j + 1] === days[j] + 1) j += 1;
    if (j - i >= 2) parts.push(`${short(days[i])}–${short(days[j])}`);
    else for (let k = i; k <= j; k += 1) parts.push(short(days[k]));
    i = j + 1;
  }
  return parts.join(", ");
};

// Days that share the same hours are listed together: [{ days: "Mon–Fri", time: "10 AM–12 PM" }].
export const classTimingGroups = (timings) => {
  const groups = new Map();
  validTimings(timings)
    .sort((a, b) => dayIndex(a.day) - dayIndex(b.day) || minutesOf(a.startTime) - minutesOf(b.startTime))
    .forEach((t) => {
      // "10 AM–1 PM": no spaces round the dash keeps it to one line in the centre table; it wraps after the dash.
      const time = `${compactTime(t.startTime)}–${compactTime(t.endTime)}`;
      const days = groups.get(time) || [];
      if (!days.includes(dayIndex(t.day))) days.push(dayIndex(t.day));
      groups.set(time, days);
    });
  return [...groups].map(([time, days]) => ({ days: dayRuns(days), time }));
};

// Whether a visit on `key` ("2026-10-15") from `start` to `end` ("HH:MM") falls inside the class timings.
// null when the Balagruha has no timings to compare with.
export const fitsClassTimings = (timings, key, start, end) => {
  const valid = validTimings(timings);
  if (!valid.length || !key || !Number.isFinite(minutesOf(start)) || !Number.isFinite(minutesOf(end))) return null;
  const weekday = (fromKey(key).getDay() + 6) % 7; // Monday = 0
  return valid.some((t) => dayIndex(t.day) === weekday && minutesOf(t.startTime) <= minutesOf(start) && minutesOf(end) <= minutesOf(t.endTime));
};

export function ClassTimings({ timings, empty = "Not set" }) {
  const groups = classTimingGroups(timings);
  if (!groups.length) return <span className="ds-timings ds-timings--empty">{empty}</span>;
  return (
    <span className="ds-timings">
      {groups.map((g) => <span key={`${g.days}|${g.time}`}><b>{g.days}</b> {g.time}</span>)}
    </span>
  );
}

// The admin endpoint groups results by day ([{ date, schedules: [...] }]); flatten to plain visits.
export const flattenSchedules = (result) => {
  const list = result?.data?.schedules || result?.data || [];
  if (!Array.isArray(list)) return [];
  return list.flatMap((item) => (Array.isArray(item?.schedules) ? item.schedules : [item]));
};

// Confirmation/result dialogs now live in utils/swalDialogs so other pages can share them.
export { confirmDialog, resultDialog, escapeHtml } from "../utils/swalDialogs";

export function Pin({ color, className = "ds-pin" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a7.5 7.5 0 0 0-7.5 7.5c0 5.4 6.4 11.6 6.7 11.9a1.2 1.2 0 0 0 1.6 0c.3-.3 6.7-6.5 6.7-11.9A7.5 7.5 0 0 0 12 2Z" fill={color} />
      <circle cx="12" cy="9.5" r="3" fill="#fff" />
    </svg>
  );
}

export function useOutsideClick(ref, onOutside, active) {
  useEffect(() => {
    if (!active) return undefined;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onOutside(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onOutside, active]);
}

// Searchable checkbox dropdown used for Balagruha and Coach pickers.
// `renderValue` lets the lesson-plan page show selected coaches as removable chips.
export function CheckPicker({ icon, options, value, onChange, multiple, searchPlaceholder, placeholder, renderValue, className = "" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useOutsideClick(ref, close, open);

  const selected = options.filter((o) => value.includes(o.id));
  const visible = options.filter((o) => o.name.toLowerCase().includes(query.trim().toLowerCase()));
  const toggle = (id) => {
    if (multiple) onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
    else { onChange([id]); setOpen(false); }
  };

  return (
    <div className={`ds-picker ${className}`} ref={ref}>
      <div role="button" tabIndex={0} className={`ds-picker__trigger${open ? " is-open" : ""}`} onClick={() => setOpen((o) => !o)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}>
        {icon}
        <span className="ds-picker__value">
          {selected.length ? (renderValue ? renderValue(selected) : selected.map((o) => o.name).join(", ")) : <span className="ds-picker__placeholder">{placeholder}</span>}
        </span>
        <ChevronDown size={16} className="ds-picker__chev" />
      </div>
      {open && (
        <div className="ds-picker__panel">
          <label className="ds-picker__search">
            <Search size={13} />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={searchPlaceholder} />
          </label>
          <ul>
            {visible.map((o) => {
              const checked = value.includes(o.id);
              return (
                <li key={o.id}>
                  <button type="button" className={checked ? "is-checked" : ""} onClick={() => toggle(o.id)}>
                    <i className="ds-checkbox">{checked && <Check size={11} strokeWidth={3.5} />}</i>
                    <span>{o.name}</span>
                    {checked && <Check size={14} className="ds-picker__tick" />}
                  </button>
                </li>
              );
            })}
            {!visible.length && <li className="ds-picker__empty">No matches</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

// A styled field that shows a formatted value but opens the browser's native date/time picker.
export function NativePickerField({ type, icon, value, display, onChange, chevron, className = "", label }) {
  const ref = useRef(null);
  const open = () => { try { ref.current?.showPicker(); } catch { ref.current?.focus(); } };
  return (
    <div className={`ds-native ${className}`} onClick={open}>
      {icon}
      <span>{display}</span>
      {chevron && <ChevronDown size={16} className="ds-native__chev" />}
      <input ref={ref} type={type} required value={value} onChange={(e) => onChange(e.target.value)} tabIndex={0} aria-label={label || (type === "date" ? "Date" : "Time")} />
    </div>
  );
}
